import type { AnalyticsConfig, AnyContext, AnyProperties, ConsentState } from "./types.js";
import type { IngestEvent } from "mindbricks-analytics-shared";
import { AUTO_EVENTS, SERVER_ONLY_EVENTS } from "mindbricks-analytics-shared";

import { LocalStore } from "./storage.js";
import { uuid } from "./eventId.js";
import { SessionManager } from "./session.js";
import { IdentityManager } from "./identity.js";
import { Attribution } from "./attribution.js";
import { Transport } from "./transport.js";
import { ConsentManager } from "./consent.js";
import { buildContext } from "./context.js";
import { attachLifecycle } from "./lifecycle.js";
import { attachAutoTracking } from "./auto-track.js";
import { attachHistoryTracking } from "./history.js";
import { CaptureResolver } from "./capture.js";

const DEFAULTS = {
  sessionTimeoutMs: 30 * 60 * 1000,
  batchSize: 25,
  flushIntervalMs: 5_000,
  persistQueue: true,
  sampleRate: 1,
  storagePrefix: "analytics",
} as const;

/**
 * Public API surface (matches the spec):
 *
 *   analytics.init()
 *   analytics.track(eventName, properties?)
 *   analytics.pageView(properties?)
 *   analytics.identify(userId, traits?)
 *   analytics.reset()
 *   analytics.flush()
 *   analytics.setConsent({ analytics, marketing })
 */
export class Analytics {
  private config: Required<Pick<AnalyticsConfig, keyof typeof DEFAULTS>> & AnalyticsConfig;
  private storage: LocalStore;
  private session!: SessionManager;
  private identity!: IdentityManager;
  private attribution!: Attribution;
  private consent!: ConsentManager;
  private transport!: Transport;
  private capture!: CaptureResolver;

  private detachLifecycle: () => void = () => {};
  private detachAutoTracking: () => void = () => {};
  private detachHistory: () => void = () => {};

  private initialized = false;
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private currentPath: string | null = null;
  private currentPathEnteredAt = 0;

  constructor(config: AnalyticsConfig) {
    if (!config.projectId) throw new Error("[analytics] projectId is required");
    if (!config.publicKey) throw new Error("[analytics] publicKey is required");
    if (!config.apiUrl) throw new Error("[analytics] apiUrl is required");

    this.config = {
      ...DEFAULTS,
      ...config,
    };
    this.storage = new LocalStore(`${this.config.storagePrefix}.${this.config.projectId}`);
  }

  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    this.session = new SessionManager(this.storage, this.config.sessionTimeoutMs);
    this.identity = new IdentityManager(this.storage);
    this.attribution = new Attribution(this.storage);
    this.consent = new ConsentManager(this.storage, this.config.defaultConsent ?? { analytics: true, marketing: false });
    this.transport = new Transport({
      apiUrl: this.config.apiUrl,
      projectId: this.config.projectId,
      publicKey: this.config.publicKey,
      batchSize: this.config.batchSize,
      flushIntervalMs: this.config.flushIntervalMs,
      persistQueue: this.config.persistQueue,
      storage: this.storage,
      ...(this.config.debug !== undefined ? { debug: this.config.debug } : {}),
    });
    this.capture = new CaptureResolver({
      storage: this.storage,
      apiUrl: this.config.apiUrl,
      projectId: this.config.projectId,
      publicKey: this.config.publicKey,
      debug: this.config.debug,
      // Dashboard-configured `userId` rule routes through identifyInternal so
      // duplicate calls are deduped and the $identify event fires only on change.
      onIdentify: (userId, traits) => this.identifyInternal(userId, traits),
    });
    this.capture.init();

    // Periodic flush as a safety net (the transport also schedules its own).
    this.flushTimer = setInterval(() => void this.transport.flush(), this.config.flushIntervalMs);

    // session_start fires on every fresh session, including the first.
    if (this.session.getSessionStartedAt() >= Date.now() - 1_000) {
      this.trackInternal(AUTO_EVENTS.SESSION_START, {});
    }

    // First page view + start page timing.
    this.session.startPageTiming();
    this.pageView();

    if (typeof window !== "undefined" && !this.config.disableAutoTracking) {
      this.detachLifecycle = attachLifecycle(this);
      this.detachAutoTracking = attachAutoTracking(this, {
        clicks: this.config.autoTracking?.clicks ?? true,
        forms: this.config.autoTracking?.forms ?? true,
        scroll: this.config.autoTracking?.scroll ?? true,
        rageClick: this.config.autoTracking?.rageClick ?? true,
        deadClick: this.config.autoTracking?.deadClick ?? true,
        errors: this.config.autoTracking?.errors ?? true,
        performance: this.config.autoTracking?.performance ?? true,
      });
      // Zero-config SPA navigation: hook the History API so $page_view fires
      // on pushState/replaceState/popstate without any router integration.
      // pageView() is idempotent for same-path calls, so RouterTracker (or
      // a manual analytics.pageView() call) won't double-emit.
      if (this.config.autoTracking?.history ?? true) {
        this.detachHistory = attachHistoryTracking(this);
      }
    }
  }

  /* ------------------------------- public API ----------------------------- */

  track(eventName: string, properties?: AnyProperties): void {
    if (!this.initialized) return;
    if (!this.consent.allowAnalytics()) return;
    if (this.shouldSample() === false) return;

    if (SERVER_ONLY_EVENTS.has(eventName)) {
      // Help the integrator catch the bug locally; backend would reject anyway.
      if (this.config.debug) {
        console.warn(`[analytics] event "${eventName}" is server-only and was dropped on the client`);
      }
      return;
    }

    const { rotated, previousSessionId } = this.session.touch();
    if (rotated && previousSessionId) {
      // session_end carries the OLD sessionId so dashboards can close out the
      // outgoing session row. session_start uses the new (current) sessionId.
      this.transport.enqueue(this.buildEvent(AUTO_EVENTS.SESSION_END, {}, previousSessionId));
      this.transport.enqueue(this.buildEvent(AUTO_EVENTS.SESSION_START, {}));
    }
    this.transport.enqueue(this.buildEvent(eventName, properties));
  }

  pageView(properties?: AnyProperties): void {
    if (!this.initialized) return;
    const newPath = typeof location !== "undefined" ? location.pathname + location.search : "";

    // Idempotent: same path → no event. This makes the auto-history hook,
    // RouterTracker, and manual pageView() calls coexist safely.
    if (this.currentPath === newPath) return;

    let timeOnPreviousPage = 0;
    let previousPath: string | null = null;
    if (this.currentPath != null && this.currentPath !== newPath) {
      timeOnPreviousPage = Date.now() - this.currentPathEnteredAt;
      previousPath = this.currentPath;
      // Emit a page_leave for the previous path with timing.
      const timing = this.session.pageTiming();
      this.transport.enqueue(
        this.buildEvent(AUTO_EVENTS.PAGE_LEAVE, {
          path: previousPath,
          ...timing,
        }),
      );
    }

    this.currentPath = newPath;
    this.currentPathEnteredAt = Date.now();
    this.session.markPageView();
    this.session.startPageTiming();

    const props: AnyProperties = {
      ...(properties ?? {}),
      previousPath,
      currentPath: newPath,
      fullUrl: typeof location !== "undefined" ? location.href : undefined,
      queryParams: this.collectQueryParams(),
      timeOnPreviousPageMs: timeOnPreviousPage || undefined,
    };

    this.track(AUTO_EVENTS.PAGE_VIEW, props);
    this.transport.enqueue(this.buildEvent(AUTO_EVENTS.PAGE_ENTER, { path: newPath }));
  }

  identify(userId: string, traits?: AnyProperties): void {
    if (!this.initialized) return;
    this.identity.identify(userId, traits);
    // Emit a special identify event so the worker can persist user_profiles.
    this.transport.enqueue(this.buildEvent(AUTO_EVENTS.IDENTIFY, traits ?? {}));
  }

  reset(): void {
    if (!this.initialized) return;
    void this.flush();
    this.identity.reset();
    this.session.reset();
    this.session.startPageTiming();
    this.currentPath = null;
  }

  async flush(): Promise<void> {
    if (!this.initialized) return;
    await this.transport.flush();
  }

  setConsent(consent: Partial<ConsentState>): void {
    if (!this.initialized) return;
    this.consent.set(consent);
  }

  /* ----------------------------- internal helpers ------------------------- */

  /** Used by lifecycle/auto-track for events that bypass sampling. */
  trackInternal(eventName: string, properties: AnyProperties): void {
    if (!this.initialized) return;
    if (!this.consent.allowAnalytics()) return;
    this.transport.enqueue(this.buildEvent(eventName, properties));
  }

  recordVisibility(visible: boolean): void {
    this.session.recordVisibility(visible);
  }

  flushBeacon(): void {
    if (!this.initialized) return;
    this.transport.flushBeacon();
  }

  queueSize(): number {
    return this.transport.size();
  }

  private buildEvent(eventName: string, properties?: AnyProperties, sessionIdOverride?: string): IngestEvent {
    const ctx: AnyContext = {
      ...buildContext(this.config.environment),
      ...this.attribution.decorateContext(),
    };
    // Dashboard-configured super properties — resolved fresh per event so
    // window globals / DOM / cookies stay current. Caller-passed properties
    // win on collision (explicit beats automatic).
    const superProps = this.capture.resolveSuper();
    const merged: AnyProperties = { ...superProps, ...(properties ?? {}) };
    const userId = this.identity.getUserId();
    const event: IngestEvent = {
      eventId: uuid(),
      eventName,
      timestamp: new Date().toISOString(),
      anonymousId: this.session.getAnonymousId(),
      sessionId: sessionIdOverride ?? this.session.getSessionId(),
      ...(Object.keys(merged).length > 0 ? { properties: merged } : {}),
      context: ctx,
    };
    if (userId) event.userId = userId;
    return event;
  }

  /**
   * Internal identify — used by the capture resolver. Dedupes if the userId
   * hasn't actually changed so we don't emit duplicate $identify events on
   * every page load.
   */
  private identifyInternal(userId: string, traits?: AnyProperties): void {
    if (!this.initialized) return;
    if (this.identity.getUserId() === userId) return;
    this.identity.identify(userId, traits);
    this.transport.enqueue(this.buildEvent(AUTO_EVENTS.IDENTIFY, traits ?? {}));
  }

  private shouldSample(): boolean {
    if (this.config.sampleRate >= 1) return true;
    return Math.random() < this.config.sampleRate;
  }

  private collectQueryParams(): Record<string, string> | undefined {
    if (typeof location === "undefined" || !location.search) return undefined;
    const out: Record<string, string> = {};
    const params = new URLSearchParams(location.search);
    for (const [k, v] of params) out[k] = v;
    return Object.keys(out).length ? out : undefined;
  }

  /* ------------------------ teardown for HMR/tests ----------------------- */

  destroy(): void {
    if (this.flushTimer) clearInterval(this.flushTimer);
    this.detachLifecycle();
    this.detachAutoTracking();
    this.detachHistory();
    this.flushTimer = null;
    this.initialized = false;
  }
}
